import React, { useState } from "react";
import {
  Archive,
  Check,
  Edit,
  GripVertical,
  MoreVertical,
  Star,
  Trash,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FAQ } from "@/types/faq";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import { TemplateTextArea } from "./TemplateTextArea";
import { useDeleteFAQ, useUpdateFAQ } from "./hooks/faqs.hooks";
import { cn } from "@/lib/utils";

interface FAQItemProps {
  faq: FAQ;
}

export const FAQItem: React.FC<FAQItemProps> = ({ faq }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editData, setEditData] = useState({
    question: faq.question || "",
    answer: faq.answer || "",
  });

  const deleteFAQ = useDeleteFAQ();
  const updateFAQ = useUpdateFAQ();
  const normalizedEditQuestion = editData.question.trim();
  const normalizedEditAnswer = editData.answer.trim();
  const showQuestionEmptyError =
    editData.question.length > 0 && normalizedEditQuestion.length === 0;

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id: faq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: "none",
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (faq.id && normalizedEditQuestion && normalizedEditAnswer) {
      updateFAQ.mutate(
        {
          id: faq.id,
          data: {
            ...editData,
            question: normalizedEditQuestion,
            answer: normalizedEditAnswer,
          },
        },
        {
          onSuccess: () => {
            setIsEditing(false);
          },
        },
      );
    }
  };

  const handleStartEdit = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setEditData({
      question: faq.question || "",
      answer: faq.answer || "",
    });
    setIsEditing(true);
  };

  const handleCancel = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditData({
      question: faq.question || "",
      answer: faq.answer || "",
    });
    setIsEditing(false);
  };

  const handleDelete = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!faq.id) return;

    deleteFAQ.mutate(faq.id, {
      onSuccess: () => setDeleteOpen(false),
    });
  };

  const handleArchive = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (faq.id) {
      updateFAQ.mutate({
        id: faq.id,
        data: { isArchived: !faq.isArchived },
      });
    }
  };

  const togglePriority = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (faq.id) {
      updateFAQ.mutate({
        id: faq.id,
        data: { isPriority: !faq.isPriority },
      });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          "border-base-border flex min-h-12 items-center gap-3 rounded-lg border bg-white px-3 py-2 transition-colors",
          faq.isArchived && "bg-secondary-badge opacity-50",
          isEditing && "border-custom-outline bg-secondary-badge",
        )}
      >
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab text-black/45 transition-colors hover:text-black/96"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="size-4" />
        </div>

        <span className="min-w-0 flex-1 truncate text-sm font-medium text-black/96">
          {faq.question}
        </span>

        <div className="flex items-center gap-1">
          {!!faq.id && (
            <Button
              variant="new_ghost"
              size="icon_sm"
              onClick={togglePriority}
              className={cn(
                faq.isPriority ? "text-royal-glow-3" : "text-muted-foreground",
              )}
              aria-label={t(
                faq.isPriority ? "Remove from priority" : "Mark as priority",
              )}
            >
              <Star fill={faq.isPriority ? "currentColor" : "none"} />
            </Button>
          )}
          {faq.isArchived ? (
            <Button
              variant="new_ghost"
              size="icon_sm"
              onClick={handleArchive}
              title={t("Restore")}
            >
              <Archive />
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="new_ghost"
                  size="icon_sm"
                  aria-label={t("FAQ actions")}
                >
                  <MoreVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={handleStartEdit}>
                    <Edit />
                    <span>{t("Edit")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleArchive}>
                    <Archive />
                    <span>{t("Archive")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDelete}
                    variant="destructive"
                  >
                    <Trash />
                    <span>{t("Delete")}</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {isEditing && (
        <div className="border-base-border bg-secondary-badge flex flex-col gap-4 rounded-xl border p-4">
          <Input
            label={t("Question")}
            placeholder={t("Question")}
            value={editData.question}
            onChange={(e) =>
              setEditData({ ...editData, question: e.target.value })
            }
            aria-invalid={showQuestionEmptyError}
          />
          {showQuestionEmptyError ? (
            <p className="text-custom-destructive-dark text-xs">
              {t("Question cannot be empty")}
            </p>
          ) : null}
          <TemplateTextArea
            label={t("Answer")}
            placeholder={t("Answer")}
            value={editData.answer}
            onChange={(val) => setEditData({ ...editData, answer: val })}
            hideVariableSelector
            actions={
              <div className="flex justify-end gap-2">
                <Button variant="new_outline" onClick={handleCancel}>
                  <X /> {t("Cancel")}
                </Button>
                <Button
                  variant="new_default"
                  onClick={handleSave}
                  disabled={
                    !normalizedEditQuestion ||
                    !normalizedEditAnswer ||
                    updateFAQ.isPending
                  }
                >
                  <Check /> {t("Save")}
                </Button>
              </div>
            }
          />
        </div>
      )}

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogOverlay />
        <DialogContent
          showCloseButton={false}
          className="border-base-border w-[425px] max-w-[calc(100vw-2rem)] gap-0 overflow-hidden rounded-xl border bg-white p-0 shadow-lg sm:max-w-[425px]"
          onClick={(event) => event.stopPropagation()}
        >
          <DialogHeader className="border-base-border gap-2 border-b px-6 py-4 text-left">
            <DialogTitle className="text-base leading-normal font-semibold text-black/96">
              {t("Delete FAQ?")}
            </DialogTitle>
            <DialogDescription className="text-sm leading-normal text-black/65">
              {t("Are you sure you want to delete this FAQ?")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="border-base-border flex-row justify-end gap-2 border-t px-6 py-4">
            <DialogClose asChild>
              <Button variant="new_outline" disabled={deleteFAQ.isPending}>
                {t("Cancel")}
              </Button>
            </DialogClose>
            <Button
              variant="new_destructive"
              onClick={handleConfirmDelete}
              disabled={deleteFAQ.isPending}
            >
              {t("Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
